import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Slider, Stack, TextField, Typography, IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import axios from 'axios';
import { clampTextSpeed, DEFAULT_TEXT_SPEED, layoutVerse, syllableIndexForProgress } from './verseSync';
import VersePortionEditor from './VersePortionEditor';
import { getStudentApiHeaders } from '../../../utils/studentPlatform';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const parseNumericId = (value) => {
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return n;
  const match = String(value || '').match(/item-(\d+)/i);
  return match ? Number(match[1]) : 0;
};

export const SPEED_OPTIONS = [0.5, 0.75, 1, 1.5, 2];

export function TextSpeedControl({ value = DEFAULT_TEXT_SPEED, onChange }) {
  const speed = clampTextSpeed(value);
  return (
    <Stack spacing={0.25} sx={{ width: '100%', pt: 0.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" fontWeight={700}>Text speed</Typography>
        <Typography variant="body2" fontWeight={700}>{speed.toFixed(2)}×</Typography>
      </Stack>
      <Slider
        size="small"
        min={0.4}
        max={2.5}
        step={0.05}
        value={speed}
        onChange={(_, next) => onChange?.(clampTextSpeed(next))}
        aria-label="Text animation speed"
      />
      <Typography variant="caption" color="text.secondary">
        Default is 1×, so the highlight finishes with this clip. Lower it if the mark runs ahead of the singing. Raise it if the mark lags. This speed is saved on the chapter.
      </Typography>
    </Stack>
  );
}

export function VerseKaraoke({ verseText = '', progress = 0, playing = false, textSpeed = DEFAULT_TEXT_SPEED, compact = false }) {
  const poem = String(verseText || '').trim();
  const layout = useMemo(() => layoutVerse(poem), [poem]);
  const activeWord = (playing || progress > 0.001) && layout.count
    ? syllableIndexForProgress(layout.count, progress, textSpeed)
    : -1;

  useEffect(() => {
    if (!poem || document.getElementById('verse-text-font')) return undefined;
    const link = document.createElement('link');
    link.id = 'verse-text-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Serif+Telugu:wght@500;600&display=swap';
    document.head.appendChild(link);
    return undefined;
  }, [poem]);

  if (!poem) return null;
  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 2.75 },
        borderRadius: 2,
        bgcolor: '#fffaf3',
        border: '1px solid #f0e0c8',
      }}
    >
      <Box
        sx={{
          fontFamily: '"Noto Serif Telugu", "Nirmala UI", "Gautami", serif',
          fontSize: { xs: '1.15rem', sm: compact ? '1.05rem' : '1.4rem' },
          fontWeight: 500,
          lineHeight: 2.3,
          textAlign: 'center',
          color: '#3d2914',
        }}
      >
        {layout.lines.map((line, lineIndex) => (
          <Box key={`line-${lineIndex}`} component="div">
            {line.map((part, partIndex) => {
              if (part.kind === 'gap') {
                return <span key={`gap-${lineIndex}-${partIndex}`}>{part.text}</span>;
              }
              if (part.kind === 'mark') {
                return <span key={`mark-${lineIndex}-${partIndex}`}>{part.text}</span>;
              }
              return (
                <Box
                  key={`word-${lineIndex}-${partIndex}`}
                  component="span"
                  sx={{
                    display: 'inline-block',
                    mx: '0.15em',
                    px: activeWord === part.start ? 0.7 : 0.15,
                    py: activeWord === part.start ? 0.15 : 0,
                    borderRadius: '8px',
                    bgcolor: activeWord === part.start ? '#ffe08a' : 'transparent',
                    color: activeWord === part.start ? '#6a3b00' : '#3d2914',
                    fontWeight: activeWord === part.start ? 700 : 500,
                    transform: activeWord === part.start ? 'scale(1.18)' : 'scale(1)',
                    transformOrigin: 'center',
                    transition: 'transform 0.12s ease, background-color 0.12s ease',
                  }}
                >
                  {part.text}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export const formatAudioTime = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
};

const storageKey = (contentId) => `lms-verse-loop-${contentId}`;

const readPrefs = (contentId) => {
  if (!contentId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(contentId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writePrefs = (contentId, prefs) => {
  if (!contentId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(contentId), JSON.stringify(prefs));
  } catch {
    // Preference storage is optional.
  }
};

/**
 * Plays one marked slice of a longer recording, repeating it a set number of times.
 * Speed is 1x, 1.5x, or 2x. Students can change the repeat count on this device.
 */
export default function VerseLoopPlayer({
  audioUrl,
  originalUrl = '',
  fullAudioUrl = '',
  startSec = 0,
  endSec = 0,
  defaultStartSec = 0,
  defaultEndSec = 0,
  defaultVerseText = '',
  defaultTextSpeed = DEFAULT_TEXT_SPEED,
  portionOverride = false,
  durationSec = 0,
  loopCount = 5,
  contentId,
  chapterId = 0,
  title = '',
  verse = true,
  verseText = '',
  textSpeed = DEFAULT_TEXT_SPEED,
  onTextSpeedChange,
  compact = false,
  allowStudentPortionEdit = false,
}) {
  const audioRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const scrubRef = useRef(false);
  const loopTargetRef = useRef(loopCount);
  const completedRef = useRef(0);
  const advancingRef = useRef(false);
  const startRef = useRef(0);
  const endRef = useRef(0);
  const usingFallbackRef = useRef(false);
  const prefs = readPrefs(contentId);
  const [speed, setSpeed] = useState(() => {
    const saved = Number(prefs?.speed);
    return SPEED_OPTIONS.includes(saved) ? saved : 1;
  });
  const [repeats, setRepeats] = useState(() => {
    const saved = Number(prefs?.repeats);
    if (Number.isFinite(saved) && saved >= 1 && saved <= 50) return saved;
    const initial = Number(loopCount) || (verse ? 5 : 1);
    return Math.min(50, Math.max(1, initial));
  });
  const [playing, setPlaying] = useState(false);
  const [finished, setFinished] = useState(false);
  const [completed, setCompleted] = useState(0);
  const [progress, setProgress] = useState(0);
  const [scrub, setScrub] = useState(null);
  const [error, setError] = useState('');
  const [activeUrl, setActiveUrl] = useState(audioUrl || originalUrl || '');
  const [clipStartSec, setClipStartSec] = useState(Number(startSec) || 0);
  const [clipEndSec, setClipEndSec] = useState(Number(endSec) || 0);
  const [isOverride, setIsOverride] = useState(Boolean(portionOverride));
  const [adjusting, setAdjusting] = useState(false);
  const [draftStart, setDraftStart] = useState(Number(startSec) || 0);
  const [draftEnd, setDraftEnd] = useState(Number(endSec) || 0);
  const [liveVerseText, setLiveVerseText] = useState(String(verseText || ''));
  const [liveTextSpeed, setLiveTextSpeed] = useState(
    clampTextSpeed(Number(textSpeed) || DEFAULT_TEXT_SPEED),
  );
  const [draftVerseText, setDraftVerseText] = useState(String(verseText || ''));
  const [draftTextSpeed, setDraftTextSpeed] = useState(
    clampTextSpeed(Number(textSpeed) || DEFAULT_TEXT_SPEED),
  );
  const [savingPortion, setSavingPortion] = useState(false);
  const [portionMessage, setPortionMessage] = useState('');

  const numericContentId = parseNumericId(contentId);
  const numericChapterId = parseNumericId(chapterId);
  const builderStart = Number(defaultStartSec) > 0 || Number(defaultEndSec) > Number(defaultStartSec)
    ? Number(defaultStartSec) || 0
    : Number(startSec) || 0;
  const builderEnd = Number(defaultEndSec) > builderStart + 0.3
    ? Number(defaultEndSec)
    : (Number(endSec) > builderStart + 0.3 ? Number(endSec) : builderStart + 0.4);
  const builderVerseText = String(defaultVerseText || verseText || '');
  const builderTextSpeed = clampTextSpeed(
    Number(defaultTextSpeed) > 0 ? Number(defaultTextSpeed) : (Number(textSpeed) || DEFAULT_TEXT_SPEED),
  );
  const canEditPortion = Boolean(
    allowStudentPortionEdit &&
    verse &&
    numericChapterId > 0 &&
    numericContentId > 0 &&
    !compact,
  );
  const fullUrl = fullAudioUrl || originalUrl || audioUrl || '';

  useEffect(() => {
    loopTargetRef.current = repeats;
  }, [repeats]);

  useEffect(() => {
    setActiveUrl(audioUrl || originalUrl || '');
    usingFallbackRef.current = false;
    setError('');
  }, [audioUrl, originalUrl]);

  useEffect(() => {
    setClipStartSec(Number(startSec) || 0);
    setClipEndSec(Number(endSec) || 0);
    setIsOverride(Boolean(portionOverride));
    setLiveVerseText(String(verseText || ''));
    setLiveTextSpeed(clampTextSpeed(Number(textSpeed) || DEFAULT_TEXT_SPEED));
  }, [startSec, endSec, portionOverride, verseText, textSpeed, contentId]);

  useEffect(() => {
    writePrefs(contentId, { speed, repeats });
  }, [contentId, speed, repeats]);

  const clipBounds = () => {
    const audio = audioRef.current;
    const start = Math.max(0, Number(clipStartSec) || 0);
    const requestedEnd = Number(clipEndSec) || 0;
    const duration = audio && Number.isFinite(audio.duration) ? audio.duration : 0;
    const end = verse && requestedEnd > start + 0.25
      ? requestedEnd
      : (duration || requestedEnd || 0);
    return { start, end };
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const syncBounds = () => {
      const { start, end } = clipBounds();
      startRef.current = start;
      endRef.current = end;
      if (scrubRef.current || pendingSeekRef.current != null) return;
      if (audio.currentTime < start - 0.05 || (end > start && audio.currentTime > end + 0.05)) {
        try { audio.currentTime = start; } catch { /* The file may still be loading. */ }
      }
    };

    const onTime = () => {
      if (scrubRef.current) return;
      if (pendingSeekRef.current != null && Math.abs(audio.currentTime - pendingSeekRef.current) > 0.45) {
        return;
      }
      pendingSeekRef.current = null;
      syncBounds();
      const start = startRef.current;
      const end = endRef.current;
      const span = Math.max(0.25, (end || audio.duration || 0) - start);
      const current = Math.max(0, audio.currentTime - start);
      setProgress(Math.min(1, current / span));

      if (advancingRef.current) {
        if (!(end > start) || audio.currentTime < end - 0.25) {
          advancingRef.current = false;
        }
        return;
      }

      if (!scrubRef.current && pendingSeekRef.current == null && end > start && audio.currentTime >= end - 0.08) {
        advancingRef.current = true;
        const done = completedRef.current + 1;
        completedRef.current = done;
        setCompleted(done);
        if (done < loopTargetRef.current) {
          audio.currentTime = start;
          const playPromise = audio.play();
          if (playPromise?.catch) playPromise.catch(() => {});
        } else {
          audio.pause();
          setPlaying(false);
          setFinished(true);
        }
      }
    };

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onError = () => {
      const fallback = originalUrl && originalUrl !== activeUrl && !usingFallbackRef.current;
      if (fallback) {
        usingFallbackRef.current = true;
        setActiveUrl(originalUrl);
        return;
      }
      setError('This audio could not be played. Check the connection and try again.');
      setPlaying(false);
    };

    const onSeeked = () => {
      if (pendingSeekRef.current == null) return;
      if (Math.abs(audio.currentTime - pendingSeekRef.current) < 0.6) {
        pendingSeekRef.current = null;
      }
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('seeked', onSeeked);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('error', onError);
    audio.playbackRate = speed;
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('seeked', onSeeked);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('error', onError);
    };
  }, [activeUrl, speed, clipStartSec, clipEndSec, verse, originalUrl]);

  useEffect(() => {
    if (!playing) return undefined;
    let frame = 0;
    const tick = () => {
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        const { start, end } = clipBounds();
        const span = Math.max(0.25, (end || audio.duration || 0) - start);
        const current = Math.max(0, audio.currentTime - start);
        if (!scrubRef.current && pendingSeekRef.current == null) setProgress(Math.min(1, current / span));
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [playing, clipStartSec, clipEndSec, verse]);

  const begin = async () => {
    const audio = audioRef.current;
    if (!audio || !activeUrl) return;
    const { start, end } = clipBounds();
    startRef.current = start;
    endRef.current = end;
    completedRef.current = 0;
    advancingRef.current = false;
    setCompleted(0);
    setFinished(false);
    setError('');
    if (audio.currentTime < start || (end > start && audio.currentTime >= end - 0.05)) {
      audio.currentTime = start;
    }
    audio.playbackRate = speed;
    try {
      await audio.play();
    } catch {
      setError('Tap play again to start this portion.');
    }
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (finished) {
      begin();
      return;
    }
    if (audio.paused) {
      const { start, end } = clipBounds();
      if (audio.currentTime < start || (end > start && audio.currentTime >= end - 0.05)) {
        audio.currentTime = start;
      }
      audio.playbackRate = speed;
      audio.play().catch(() => setError('Tap play again to start this portion.'));
    } else {
      audio.pause();
    }
  };

  const changeSpeed = (next) => {
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const reduceSpeed = () => {
    const index = SPEED_OPTIONS.indexOf(speed);
    if (index > 0) changeSpeed(SPEED_OPTIONS[index - 1]);
  };

  const changeRepeats = (delta) => {
    setRepeats((prev) => {
      const next = Math.min(50, Math.max(1, prev + delta));
      loopTargetRef.current = next;
      if (finished && next > completedRef.current) setFinished(false);
      return next;
    });
  };

  const spanLabel = verse && Number(clipEndSec) > Number(clipStartSec)
    ? `${formatAudioTime(clipStartSec)} – ${formatAudioTime(clipEndSec)}`
    : 'Full recording';
  const poem = String(liveVerseText || '').trim();

  const openAdjustPortion = () => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    setAdjusting(true);
    setPortionMessage('');
    setDraftStart(builderStart);
    setDraftEnd(builderEnd > builderStart + 0.3 ? builderEnd : builderStart + 0.4);
    setDraftVerseText(builderVerseText);
    setDraftTextSpeed(builderTextSpeed);
  };

  const saveStudentPortion = async () => {
    if (!(draftEnd > draftStart + 0.3)) {
      setPortionMessage('End must be more than 0.3s after start.');
      return;
    }
    setSavingPortion(true);
    setPortionMessage('');
    try {
      const response = await axios.put(
        `${getApiBaseUrl()}/lms/chapters/${numericChapterId}/content/${numericContentId}/verse-portion`,
        {
          startSec: draftStart,
          endSec: draftEnd,
          verseText: String(draftVerseText || '').trim(),
          textSpeed: clampTextSpeed(draftTextSpeed),
        },
        { headers: getStudentApiHeaders() },
      );
      const data = response?.data?.data || {};
      const nextStart = Number(data.startSec ?? draftStart);
      const nextEnd = Number(data.endSec ?? draftEnd);
      const nextText = String(data.verseText ?? draftVerseText ?? '');
      const nextSpeed = clampTextSpeed(Number(data.textSpeed) || draftTextSpeed);
      setClipStartSec(nextStart);
      setClipEndSec(nextEnd);
      setLiveVerseText(nextText);
      setLiveTextSpeed(nextSpeed);
      setIsOverride(true);
      setAdjusting(false);
      setCompleted(0);
      setFinished(false);
      setProgress(0);
      setPortionMessage('Saved for you only. Other students keep the teacher defaults.');
      const audio = audioRef.current;
      if (audio) {
        try { audio.currentTime = nextStart; } catch { /* ignore */ }
      }
    } catch (err) {
      setPortionMessage(
        err?.response?.data?.message || 'Could not save your portion. Try again.',
      );
    } finally {
      setSavingPortion(false);
    }
  };

  const resetToBuilderPortion = async () => {
    setSavingPortion(true);
    setPortionMessage('');
    try {
      const response = await axios.delete(
        `${getApiBaseUrl()}/lms/chapters/${numericChapterId}/content/${numericContentId}/verse-portion`,
        { headers: getStudentApiHeaders() },
      );
      const data = response?.data?.data || {};
      const nextStart = Number(data.startSec ?? builderStart);
      const nextEnd = Number(data.endSec ?? builderEnd);
      const nextText = String(data.verseText ?? builderVerseText ?? '');
      const nextSpeed = clampTextSpeed(Number(data.textSpeed) || builderTextSpeed);
      setClipStartSec(nextStart);
      setClipEndSec(nextEnd);
      setLiveVerseText(nextText);
      setLiveTextSpeed(nextSpeed);
      setIsOverride(false);
      setAdjusting(false);
      setDraftStart(nextStart);
      setDraftEnd(nextEnd);
      setDraftVerseText(nextText);
      setDraftTextSpeed(nextSpeed);
      setCompleted(0);
      setFinished(false);
      setProgress(0);
      setPortionMessage('Back to teacher defaults (portion, text, and speed).');
      const audio = audioRef.current;
      if (audio) {
        try { audio.currentTime = nextStart; } catch { /* ignore */ }
      }
    } catch (err) {
      setPortionMessage(
        err?.response?.data?.message || 'Could not reset to teacher defaults.',
      );
    } finally {
      setSavingPortion(false);
    }
  };

  const movePlayhead = (fraction, shouldPlay) => {
    const audio = audioRef.current;
    if (!audio || !activeUrl) return;
    const { start, end } = clipBounds();
    const span = Math.max(0.25, (end > start ? end - start : (audio.duration || 0) - start));
    const fractionClamped = Math.min(1, Math.max(0, Number(fraction) || 0));
    const next = Math.max(start, Math.min(end > start ? end - 0.12 : start + span, start + fractionClamped * span));
    pendingSeekRef.current = next;
    setProgress(fractionClamped);
    if (finished) {
      setFinished(false);
      completedRef.current = Math.max(0, completedRef.current - 1);
      setCompleted(completedRef.current);
    }
    const write = () => {
      const node = audioRef.current;
      if (!node || pendingSeekRef.current == null) return;
      try { node.currentTime = pendingSeekRef.current; } catch { /* The file may still be loading. */ }
      if (!shouldPlay) return;
      node.playbackRate = speed;
      const playPromise = node.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    };
    window.setTimeout(() => {
      if (pendingSeekRef.current === next) pendingSeekRef.current = null;
    }, 900);
    if (audio.readyState >= 1) write();
    else audio.addEventListener('loadedmetadata', write, { once: true });
  };

  const shownFraction = scrub ?? progress;
  const clipStart = Math.max(0, Number(clipStartSec) || 0);
  const clipEnd = Number(clipEndSec) || 0;
  const positionLabel = verse && clipEnd > clipStart + 0.25
    ? formatAudioTime(clipStart + shownFraction * (clipEnd - clipStart))
    : '';

  return (
    <Stack spacing={compact ? 1 : 2} sx={{ width: '100%' }}>
      {poem ? (
        <Stack spacing={1}>
          <VerseKaraoke
            verseText={poem}
            progress={progress}
            playing={playing}
            textSpeed={liveTextSpeed}
            compact={compact}
          />
          {onTextSpeedChange ? (
            <TextSpeedControl value={liveTextSpeed} onChange={onTextSpeedChange} />
          ) : null}
        </Stack>
      ) : null}
      {title && !compact ? (
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary">{spanLabel}</Typography>
      {isOverride ? (
        <Typography variant="caption" color="primary.main">
          Using your cut · other students still hear the builder portion
        </Typography>
      ) : null}
      <audio ref={audioRef} src={activeUrl} preload="metadata" />

      <Stack direction="row" spacing={1.5} alignItems="center">
        <IconButton
          color="primary"
          onClick={toggle}
          disabled={!activeUrl}
          sx={{
            width: compact ? 44 : 64,
            height: compact ? 44 : 64,
            bgcolor: 'primary.main',
            color: '#fff',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          {playing ? <PauseIcon sx={{ fontSize: compact ? 26 : 36 }} /> : <PlayArrowIcon sx={{ fontSize: compact ? 26 : 36 }} />}
        </IconButton>
        <Box
          sx={{ flex: 1, minWidth: 0, px: 1 }}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <Slider
            min={0}
            max={1000}
            step={1}
            value={Math.round(shownFraction * 1000)}
            disabled={!activeUrl}
            aria-label="Play position"
            onChange={(_, value) => {
              const next = (Array.isArray(value) ? value[0] : value) / 1000;
              scrubRef.current = true;
              setScrub(next);
              movePlayhead(next, false);
            }}
            onChangeCommitted={(_, value) => {
              const next = (Array.isArray(value) ? value[0] : value) / 1000;
              scrubRef.current = false;
              setScrub(null);
              movePlayhead(next, true);
            }}
            sx={{
              py: 1.5,
              '& .MuiSlider-thumb': {
                width: 28,
                height: 28,
              },
              '& .MuiSlider-rail': { height: 12, opacity: 1, bgcolor: '#e3f2fd' },
              '& .MuiSlider-track': { height: 12, border: 'none' },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
            {positionLabel ? `${positionLabel} · ` : ''}
            {finished
              ? `Finished ${repeats} time${repeats === 1 ? '' : 's'}`
              : `Play ${Math.min(completed + 1, repeats)} of ${repeats}`}
          </Typography>
        </Box>
        <Button startIcon={<ReplayIcon />} onClick={begin} disabled={!activeUrl}>
          Again
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" fontWeight={600} sx={{ mr: 0.5 }}>Speed</Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={reduceSpeed}
          disabled={speed <= SPEED_OPTIONS[0]}
          aria-label="Reduce play speed"
        >
          -x
        </Button>
        {SPEED_OPTIONS.map((option) => (
          <Button
            key={option}
            size="small"
            variant={speed === option ? 'contained' : 'outlined'}
            onClick={() => changeSpeed(option)}
          >
            {option}x
          </Button>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" fontWeight={600}>Loop</Typography>
        <IconButton size="small" onClick={() => changeRepeats(-1)} disabled={repeats <= 1}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Typography variant="body1" fontWeight={700} sx={{ minWidth: 24, textAlign: 'center' }}>
          {repeats}
        </Typography>
        <IconButton size="small" onClick={() => changeRepeats(1)} disabled={repeats >= 50}>
          <AddIcon fontSize="small" />
        </IconButton>
        <Typography variant="caption" color="text.secondary">times on this device</Typography>
      </Stack>

      {canEditPortion ? (
        <Stack spacing={1.25} sx={{ pt: 0.5 }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCutIcon />}
              onClick={openAdjustPortion}
              disabled={savingPortion}
            >
              {isOverride ? 'Edit my portion' : 'Adjust my portion'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={resetToBuilderPortion}
              disabled={savingPortion || !isOverride}
            >
              Use teacher defaults
            </Button>
          </Stack>

          {adjusting ? (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: { xs: 1.25, sm: 2 },
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Adjust my portion
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Full song is open. Teacher cut / text / speed are pre-filled — change any of them for yourself only.
              </Typography>
              <Typography variant="caption" color="primary.main" display="block" sx={{ mb: 1 }}>
                Teacher portion: {formatAudioTime(builderStart)} – {formatAudioTime(builderEnd)}
              </Typography>
              <VersePortionEditor
                key={`portion-edit-${numericContentId}-open`}
                audioUrl={fullUrl}
                originalUrl={fullUrl}
                startSec={draftStart}
                endSec={draftEnd}
                durationSec={durationSec}
                onChange={({ startSec: nextStart, endSec: nextEnd }) => {
                  setDraftStart(Number(nextStart) || 0);
                  setDraftEnd(Number(nextEnd) || 0);
                }}
              />
              <TextField
                label="Sloka / verse text"
                value={draftVerseText}
                onChange={(event) => setDraftVerseText(event.target.value)}
                multiline
                minRows={3}
                fullWidth
                sx={{ mt: 2 }}
                helperText="Teacher text is the default. Edit for yourself only."
              />
              <Box sx={{ mt: 1.5 }}>
                <TextSpeedControl
                  value={draftTextSpeed}
                  onChange={setDraftTextSpeed}
                />
              </Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ mt: 1.5 }}
              >
                <Button
                  variant="contained"
                  onClick={saveStudentPortion}
                  disabled={savingPortion || !(draftEnd > draftStart + 0.3)}
                >
                  {savingPortion ? 'Saving…' : 'Save for me only'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  onClick={resetToBuilderPortion}
                  disabled={savingPortion}
                >
                  Use teacher defaults
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setDraftStart(builderStart);
                    setDraftEnd(builderEnd);
                    setDraftVerseText(builderVerseText);
                    setDraftTextSpeed(builderTextSpeed);
                  }}
                  disabled={savingPortion}
                >
                  Reselect teacher defaults
                </Button>
                <Button
                  variant="text"
                  onClick={() => setAdjusting(false)}
                  disabled={savingPortion}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : null}
        </Stack>
      ) : null}

      {portionMessage ? (
        <Alert severity="info" onClose={() => setPortionMessage('')}>
          {portionMessage}
        </Alert>
      ) : null}

      {error ? (
        <Typography variant="body2" color="error">{error}</Typography>
      ) : null}
    </Stack>
  );
}
